<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { parsePhoneNumber, isValidPhoneNumber } from 'libphonenumber-js';
import * as XLSX from 'xlsx';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['update:modelValue']);
const { t } = useI18n();

const fileInput = ref(null);
const isProcessing = ref(false);
const uploadError = ref('');
const uploadStats = ref(null);

const entryCount = computed(() => props.modelValue.length);
const skippedCount = computed(() =>
  uploadStats.value
    ? uploadStats.value.invalid + uploadStats.value.missingName
    : 0
);

const NUMBER_HEADERS = [
  'number',
  'numero',
  'número',
  'phone',
  'phone_number',
  'telefono',
  'teléfono',
  'celular',
  'whatsapp',
];
const NAME_HEADERS = ['name', 'nombre', 'contact_name', 'full_name'];

const normalizeHeader = value =>
  String(value ?? '')
    .trim()
    .toLowerCase();

const detectColumns = headerRow => {
  const headers = headerRow.map(normalizeHeader);
  const numberIndex = headers.findIndex(header =>
    NUMBER_HEADERS.includes(header)
  );
  const nameIndex = headers.findIndex(header => NAME_HEADERS.includes(header));
  return {
    numberIndex,
    nameIndex,
    hasHeader: numberIndex !== -1 && nameIndex !== -1,
  };
};

const sanitizeNumber = value => {
  let str = String(value ?? '').trim();
  if (!str) return '';
  // Quitar todo lo que no sea + o dígito
  str = str.replace(/[^\d+]/g, '');
  if (!str) return '';
  if (!str.startsWith('+') && /^\d+$/.test(str)) {
    str = `+${str}`;
  }
  return str;
};

const validateNumber = number => {
  try {
    if (!number.startsWith('+')) return null;
    if (!isValidPhoneNumber(number)) return null;
    return parsePhoneNumber(number).number;
  } catch (error) {
    return null;
  }
};

const parseFile = file =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = e => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(firstSheet, {
          header: 1,
          defval: '',
        });
        resolve(
          rows.filter(
            row =>
              Array.isArray(row) &&
              row.some(cell => String(cell ?? '').trim() !== '')
          )
        );
      } catch (error) {
        reject(new Error(t('CAMPAIGN.QRBUHO.FILE_AUDIENCE.ERROR.PARSE')));
      }
    };

    reader.onerror = () =>
      reject(new Error(t('CAMPAIGN.QRBUHO.FILE_AUDIENCE.ERROR.READ')));
    reader.readAsArrayBuffer(file);
  });

const buildEntries = rows => {
  if (rows.length === 0) return { entries: [], invalid: [], missingName: 0 };

  const { numberIndex, nameIndex, hasHeader } = detectColumns(rows[0]);
  const useNumberIndex = hasHeader ? numberIndex : 0;
  const useNameIndex = hasHeader ? nameIndex : 1;
  const dataRows = hasHeader ? rows.slice(1) : rows;

  const entries = [];
  const invalid = [];
  let missingName = 0;
  const seen = new Set();

  dataRows.forEach((row, idx) => {
    const rawNumber = row[useNumberIndex];
    const rawName = row[useNameIndex];
    const cleaned = sanitizeNumber(rawNumber);
    const valid = cleaned ? validateNumber(cleaned) : null;
    const name = String(rawName ?? '').trim();

    if (!valid) {
      invalid.push({
        row: idx + (hasHeader ? 2 : 1),
        value: String(rawNumber ?? '').trim(),
      });
      return;
    }

    if (!name) {
      missingName += 1;
      return;
    }

    if (seen.has(valid)) return;
    seen.add(valid);
    entries.push({ number: valid, name });
  });

  return { entries, invalid, missingName };
};

const handleFileUpload = async event => {
  const file = event.target.files[0];
  if (!file) return;

  uploadError.value = '';
  uploadStats.value = null;
  isProcessing.value = true;

  try {
    const validExtensions = ['.xlsx', '.xls', '.csv'];
    const fileName = file.name.toLowerCase();
    const isValidFile = validExtensions.some(ext => fileName.endsWith(ext));
    if (!isValidFile) {
      throw new Error(t('CAMPAIGN.QRBUHO.FILE_AUDIENCE.ERROR.INVALID_TYPE'));
    }

    const rows = await parseFile(file);
    if (rows.length === 0) {
      throw new Error(t('CAMPAIGN.QRBUHO.FILE_AUDIENCE.ERROR.EMPTY'));
    }

    const { entries, invalid, missingName } = buildEntries(rows);
    uploadStats.value = {
      total: entries.length + invalid.length + missingName,
      valid: entries.length,
      invalid: invalid.length,
      missingName,
      invalidRows: invalid,
    };

    emit('update:modelValue', entries);
  } catch (error) {
    uploadError.value = error.message;
    emit('update:modelValue', []);
  } finally {
    isProcessing.value = false;
    if (fileInput.value) fileInput.value.value = '';
  }
};

const downloadTemplate = () => {
  const templateData = [
    ['number', 'name'],
    ['+51999999999', 'Juan Pérez'],
    ['+5215512345678', 'María López'],
  ];
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet(templateData);
  ws['!cols'] = [{ wch: 20 }, { wch: 24 }];
  XLSX.utils.book_append_sheet(wb, ws, 'Audiencia');
  XLSX.writeFile(wb, 'qrbuho_audiencia_template.xlsx');
};

const triggerFileInput = () => fileInput.value?.click();

const clearEntries = () => {
  emit('update:modelValue', []);
  uploadStats.value = null;
  uploadError.value = '';
};
</script>

<template>
  <div class="space-y-3">
    <div class="grid grid-cols-2 gap-2">
      <Button
        variant="outline"
        size="sm"
        icon="i-lucide-download"
        type="button"
        class="w-full"
        @click="downloadTemplate"
      >
        {{ t('CAMPAIGN.QRBUHO.FILE_AUDIENCE.DOWNLOAD_TEMPLATE') }}
      </Button>

      <Button
        variant="outline"
        size="sm"
        icon="i-lucide-upload"
        type="button"
        class="w-full"
        :is-loading="isProcessing"
        @click="triggerFileInput"
      >
        {{ t('CAMPAIGN.QRBUHO.FILE_AUDIENCE.UPLOAD_FILE') }}
      </Button>

      <input
        ref="fileInput"
        type="file"
        accept=".xlsx,.xls,.csv"
        class="hidden"
        @change="handleFileUpload"
      />
    </div>

    <div
      v-if="uploadStats"
      class="rounded-lg border p-3"
      :class="{
        'border-n-green-6 bg-n-green-3':
          uploadStats.invalid === 0 && uploadStats.missingName === 0,
        'border-n-yellow-6 bg-n-yellow-3':
          uploadStats.invalid > 0 || uploadStats.missingName > 0,
      }"
    >
      <div class="flex items-center justify-between gap-2 text-sm">
        <span class="flex items-center gap-2 font-medium">
          <Icon
            :icon="
              uploadStats.invalid === 0 && uploadStats.missingName === 0
                ? 'i-lucide-check-circle'
                : 'i-lucide-alert-triangle'
            "
            class="size-4 flex-shrink-0"
            :class="{
              'text-n-green-11':
                uploadStats.invalid === 0 && uploadStats.missingName === 0,
              'text-n-yellow-11':
                uploadStats.invalid > 0 || uploadStats.missingName > 0,
            }"
          />
          <span class="text-n-slate-12">
            {{
              t('CAMPAIGN.QRBUHO.FILE_AUDIENCE.LOADED_OK', {
                count: uploadStats.valid,
              })
            }}
          </span>
        </span>
        <button
          v-if="entryCount > 0"
          type="button"
          class="text-xs text-n-slate-10 hover:text-n-slate-12 underline"
          @click="clearEntries"
        >
          {{ t('CAMPAIGN.QRBUHO.FILE_AUDIENCE.CLEAR') }}
        </button>
      </div>
      <p v-if="skippedCount > 0" class="mt-1 ml-6 text-xs text-n-yellow-12">
        {{
          t('CAMPAIGN.QRBUHO.FILE_AUDIENCE.SKIPPED', { count: skippedCount })
        }}
      </p>
    </div>

    <div
      v-if="uploadError"
      class="flex items-start gap-2 rounded-lg border border-n-red-6 bg-n-red-3 p-3 text-sm"
    >
      <Icon
        icon="i-lucide-x-circle"
        class="size-4 flex-shrink-0 mt-0.5 text-n-red-11"
      />
      <p class="text-n-red-12">{{ uploadError }}</p>
    </div>

    <div
      class="text-xs text-n-slate-11 bg-n-blue-3 border border-n-blue-6 rounded-lg p-3"
    >
      <Icon icon="i-lucide-info" class="inline-block mr-1" />
      {{ t('CAMPAIGN.QRBUHO.FILE_AUDIENCE.HELP') }}
    </div>
  </div>
</template>
